import React, { useState, useMemo } from "react";
import { useQuery, useAction } from "wasp/client/operations";
import { getJobs, deleteJob } from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { DashboardLayout } from "../../client/components/Sidebar";
import { useLocationContext } from "../../client/contexts/LocationContext";
import { useRequireSectionVisible } from "../../client/hooks/useRequireSectionVisible";
import {
  EnhancedPageLayout,
  EnhancedActionButton,
  EnhancedIconButton,
  enhancedPageLayoutStyles,
  useResponsiveLayout,
} from "../../components/ui/enhanced-page-layout";
import {
  Search,
  Filter,
  Plus,
  Eye,
  Edit,
  Trash2,
  DollarSign,
  Tag,
  Phone,
  Clock,
  Star,
  Award,
  Grid3X3,
  List,
  Table,
  Mail,
} from "lucide-react";

import { Button } from "../../components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../../components/ui/sheet";
import { cn } from "../../lib/utils";

export default function JobsPage() {
  useRequireSectionVisible("showJob");
  const { data: user } = useAuth();
  const { selectedLocation, locations } = useLocationContext();
  const navigate = useNavigate();
  const deleteJobFn = useAction(deleteJob);

  // State for filters and view mode
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedListingType, setSelectedListingType] = useState<string>("all");
  const [selectedPriceType, setSelectedPriceType] = useState<string>("all");
  const [sortBy, setSortBy] = useState<"newest" | "oldest" | "title" | "price">(
    "newest",
  );

  // Use responsive layout hook
  const {
    viewMode,
    setViewMode,
    isFilterExpanded,
    setIsFilterExpanded,
    isMobile,
    shouldCollapseSidebar,
    hideViewSwitcher,
  } = useResponsiveLayout("table");

  const defaultLocation = useMemo(() => {
    return (locations as any[]).find((l: any) => l.locationCode === "UNI-MAIN-001" || l.name?.includes("Main Campus")) || locations[0];
  }, [locations]);

  const effectiveLocation = selectedLocation || defaultLocation;

  const {
    data: jobs = [],
    isLoading,
    error,
  } = useQuery(getJobs, {
    locationId: effectiveLocation?.id || "",
    filters: {
      listingType:
        selectedListingType === "all"
          ? undefined
          : (selectedListingType as "OFFER" | "REQUEST"),
      priceType: selectedPriceType === "all" ? undefined : selectedPriceType,
    },
  });

  const filteredJobs = useMemo(() => {
    let filtered = jobs as any[];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (job: any) =>
          job.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          job.description?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    // Sort
    switch (sortBy) {
      case "newest":
        filtered = [...filtered].sort(
          (a: any, b: any) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
        break;
      case "oldest":
        filtered = [...filtered].sort(
          (a: any, b: any) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        );
        break;
      case "title":
        filtered = [...filtered].sort((a: any, b: any) =>
          a.title.localeCompare(b.title),
        );
        break;
      case "price":
        filtered = [...filtered].sort(
          (a: any, b: any) =>
            (b.metadata?.price || 0) - (a.metadata?.price || 0),
        );
        break;
    }

    return filtered;
  }, [jobs, searchTerm, selectedListingType, selectedPriceType, sortBy]);

  const handleDelete = async (jobId: string) => {
    if (window.confirm("Are you sure you want to delete this job?")) {
      try {
        await deleteJobFn({ itemId: jobId });
      } catch (error) {
        console.error("Error deleting job:", error);
        alert("Error deleting job. Please try again.");
      }
    }
  };

  const getPriceTypeColor = (priceType: string) => {
    const colors = {
      HOURLY: "bg-blue-100 text-blue-800",
      FIXED: "bg-green-100 text-green-800",
      PER_SESSION: "bg-purple-100 text-purple-800",
      FREE: "bg-gray-100 text-gray-800",
    };
    return (
      colors[priceType as keyof typeof colors] || "bg-gray-100 text-gray-800"
    );
  };

  const getListingTypeColor = (type: string) => {
    return type === "OFFER"
      ? "bg-emerald-100 text-emerald-800"
      : "bg-amber-100 text-amber-800";
  };

  const headerContent = (
    <div className="px-4 py-3">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Jobs</h1>
          <p className="text-xs sm:text-sm text-gray-600 mt-1">
            Discover jobs and job requests at {effectiveLocation?.name || "All Locations"} (
            {filteredJobs.length} items)
          </p>
        </div>
        <div className="flex items-center gap-3">
          <EnhancedActionButton
            onClick={() => setIsFilterExpanded(!isFilterExpanded)}
            icon={Filter}
            label=""
            variant={isFilterExpanded ? "primary" : "secondary"}
            className="hidden lg:flex"
          />

          {/* Mobile Filter Button */}
          <Sheet open={isFilterExpanded && isMobile} onOpenChange={setIsFilterExpanded}>
            <EnhancedActionButton
              onClick={() => setIsFilterExpanded(true)}
              icon={Filter}
              label=""
              variant={isFilterExpanded ? "primary" : "secondary"}
              className="lg:hidden"
            />
            <SheetContent side="right" className="w-[300px] sm:w-[400px]">
              <SheetHeader>
                <SheetTitle>Filters</SheetTitle>
              </SheetHeader>
              <div className="py-6 space-y-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Search</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                      type="text"
                      placeholder="Search jobs..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Listing Type</label>
                  <select
                    value={selectedListingType}
                    onChange={(e) => setSelectedListingType(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Types</option>
                    <option value="OFFER">Offers</option>
                    <option value="REQUEST">Requests</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Price Type</label>
                  <select
                    value={selectedPriceType}
                    onChange={(e) => setSelectedPriceType(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Price Types</option>
                    <option value="HOURLY">Hourly</option>
                    <option value="FIXED">Fixed</option>
                    <option value="PER_SESSION">Per Session</option>
                    <option value="FREE">Free</option>
                  </select>
                </div>

                <Button 
                  className="w-full" 
                  onClick={() => setIsFilterExpanded(false)}
                >
                  Apply Filters
                </Button>
              </div>
            </SheetContent>
          </Sheet>
          {user?.isAdmin && (
            <EnhancedActionButton
              onClick={() => {
                if (effectiveLocation?.locationCode) {
                  navigate(
                    `/locations/${effectiveLocation.locationCode}/jobs/add`,
                  );
                } else {
                  navigate("/jobs/add");
                }
              }}
              icon={Plus}
              label={<span className="hidden sm:inline">Add Job</span>}
            />
          )}
        </div>
      </div>
    </div>
  );

  const filtersContent = (
    <div 
      className={cn(
        "bg-sidebar-hover border-t border-border transition-all duration-300 overflow-hidden hidden lg:block",
        isFilterExpanded ? "max-h-[500px]" : "max-h-0 border-none"
      )}
    >
      <div className="px-6 py-3 space-y-3">
        {/* Column Headers - Only for table view */}
        {viewMode === "table" && (
          <div className="grid grid-cols-12 gap-4 text-xs font-medium text-content-text-muted uppercase tracking-wider select-none cursor-default">
            <div className="col-span-4 min-w-0 truncate">Job</div>
            <div className="col-span-2 min-w-0 truncate">Type</div>
            <div className="col-span-1 min-w-0 truncate">Price Type</div>
            <div className="col-span-1 min-w-0 truncate">Price</div>
            <div className={user?.isAdmin ? "col-span-2 min-w-0 truncate" : "col-span-4 min-w-0 truncate"}>Provider</div>
            {user?.isAdmin && <div className="col-span-2 text-right min-w-0 truncate">Actions</div>}
          </div>
        )}
        
        {/* Column Filters */}
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search jobs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="col-span-2">
            <select
              value={selectedListingType}
              onChange={(e) => setSelectedListingType(e.target.value)}
              className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All</option>
              <option value="OFFER">Offer</option>
              <option value="REQUEST">Request</option>
            </select>
          </div>
          <div className="col-span-1">
            <select
              value={selectedPriceType}
              onChange={(e) => setSelectedPriceType(e.target.value)}
              className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All</option>
              <option value="HOURLY">Hourly</option>
              <option value="FIXED">Fixed</option>
              <option value="PER_SESSION">Per Session</option>
              <option value="FREE">Free</option>
            </select>
          </div>
          <div className="col-span-1">
            <select
              value={sortBy === "price" ? "price" : "all"}
              onChange={(e) =>
                setSortBy(e.target.value === "price" ? "price" : "newest")
              }
              className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All</option>
              <option value="price">High-Low</option>
            </select>
          </div>
          <div className={user?.isAdmin ? "col-span-2" : "col-span-4"}>
            <select
              value={
                sortBy === "newest" ||
                sortBy === "oldest" ||
                sortBy === "title"
                  ? sortBy
                  : "newest"
              }
              onChange={(e) => setSortBy(e.target.value as any)}
              className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
              <option value="title">A-Z</option>
            </select>
          </div>
          {user?.isAdmin && <div className="col-span-2" />}
        </div>
      </div>
    </div>
  );

  return (
    <DashboardLayout
      forceSidebarCollapse={shouldCollapseSidebar}
      showViewSwitcher={!hideViewSwitcher}
      activeView={viewMode}
      onViewChange={setViewMode}
    >
      <EnhancedPageLayout
        header={headerContent}
        filters={filtersContent}
      >
        <div className="w-full">
          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-600 mt-2">Loading jobs...</p>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Award className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Error Loading Jobs
                </h3>
                <p className="text-gray-600">Please try again later.</p>
              </div>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && !error && filteredJobs.length === 0 && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Award className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No Jobs Found
                </h3>
                <p className="text-gray-600">
                  {searchTerm || selectedPriceType !== "all"
                    ? "Try adjusting your search or filters to find what you're looking for."
                    : "No jobs have been offered in this location yet."}
                </p>
              </div>
            </div>
          )}

          {/* Content */}
          {!isLoading && !error && filteredJobs.length > 0 && (
            <>
              {/* Cards View */}
              {viewMode === "cards" && (
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredJobs.map((job) => (
                      <div
                        key={job.id}
                        className={enhancedPageLayoutStyles.card.container}
                        onClick={() => navigate(`/jobs/${job.id}`)}
                      >
                        <div className={enhancedPageLayoutStyles.card.content}>
                          <div className={enhancedPageLayoutStyles.card.header}>
                            <div className="flex items-center gap-2">
                              {job.metadata?.listingType && (
                                <span
                                  className={`px-2 py-1 text-xs font-medium rounded ${getListingTypeColor(
                                    job.metadata.listingType,
                                  )}`}
                                >
                                  {job.metadata.listingType}
                                </span>
                              )}
                              <span
                                className={`px-2 py-1 text-xs font-medium rounded ${getPriceTypeColor(
                                  job.metadata?.priceType,
                                )}`}
                              >
                                {job.metadata?.priceType?.replace("_", " ")}
                              </span>
                            </div>
                            <div className="text-lg font-bold text-green-600">
                              {job.metadata?.price && job.metadata.priceType !== "FREE" ? `$${job.metadata.price}` : "Free"}
                            </div>
                          </div>

                          <h3 className={enhancedPageLayoutStyles.card.title}>
                            {job.title}
                          </h3>

                          <p className={enhancedPageLayoutStyles.card.description}>
                            {job.description}
                          </p>

                          <div className={enhancedPageLayoutStyles.card.footer}>
                            <div className={enhancedPageLayoutStyles.card.meta}>
                              by {job.author.name || job.author.email}
                            </div>
                            <div className="flex items-center gap-2" onClick={(e) => e?.stopPropagation()}>
                              <EnhancedIconButton
                                onClick={() => navigate(`/jobs/${job.id}`)}
                                icon={Eye}
                                title="View details"
                              />
                              {user?.isAdmin && (
                                <EnhancedIconButton
                                  onClick={() => handleDelete(job.id)}
                                  icon={Trash2}
                                  title="Delete"
                                  variant="danger"
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* List View */}
              {viewMode === "list" && (
                <div className="p-6 space-y-4">
                  {filteredJobs.map((job) => (
                    <div
                      key={job.id}
                      className={enhancedPageLayoutStyles.list.item}
                      onClick={() => navigate(`/jobs/${job.id}`)}
                    >
                      <div className={enhancedPageLayoutStyles.list.content}>
                        <div className={enhancedPageLayoutStyles.list.left}>
                          <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
                            <Award className="w-6 h-6 text-blue-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">
                              {job.title}
                            </h3>
                            <div className="flex items-center gap-2 mt-1">
                              <span className={cn("px-2 py-0.5 rounded text-[10px] font-medium", getListingTypeColor(job.metadata?.listingType))}>
                                {job.metadata?.listingType}
                              </span>
                              <span className="text-xs text-gray-500">
                                {job.author.name || job.author.email} • {new Date(job.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className={enhancedPageLayoutStyles.list.right}>
                          <div className="text-right mr-4">
                            <span className="block text-sm font-bold text-green-600">
                              {job.metadata?.price ? `$${job.metadata.price}` : "Free"}
                            </span>
                          </div>
                          <EnhancedIconButton
                            onClick={(e) => {
                              e?.stopPropagation();
                              navigate(`/jobs/${job.id}`);
                            }}
                            icon={Eye}
                            title="View"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Table View */}
              {viewMode === "table" && (
                <div className="w-full">
                  <div className={enhancedPageLayoutStyles.table.container}>
                    <div className={enhancedPageLayoutStyles.table.wrapper}>
                      <table className={enhancedPageLayoutStyles.table.table}>
                        <tbody className={enhancedPageLayoutStyles.table.tbody}>
                          {filteredJobs.map((job) => (
                            <tr
                              key={job.id}
                              className="group hover:bg-sidebar-hover transition-colors cursor-pointer"
                              onClick={() => navigate(`/jobs/${job.id}`)}
                            >
                              <td className={cn(enhancedPageLayoutStyles.table.td, "col-span-4")}>
                                <div className="px-6 py-4">
                                  <div className="font-medium text-gray-900 truncate">{job.title}</div>
                                  <div className="text-xs text-gray-500 truncate">{job.description}</div>
                                </div>
                              </td>
                              <td className={cn(enhancedPageLayoutStyles.table.td, "col-span-2")}>
                                <div className="px-6 py-4">
                                  <span className={cn("px-2 py-1 rounded-full text-[10px] font-medium", getListingTypeColor(job.metadata?.listingType))}>
                                    {job.metadata?.listingType}
                                  </span>
                                </div>
                              </td>
                              <td className={cn(enhancedPageLayoutStyles.table.td, "col-span-1")}>
                                <div className="px-6 py-4 text-xs text-gray-600">
                                  {job.metadata?.priceType?.replace("_", " ")}
                                </div>
                              </td>
                              <td className={cn(enhancedPageLayoutStyles.table.td, "col-span-1")}>
                                <div className="px-6 py-4 text-sm font-bold text-green-600">
                                  {job.metadata?.price ? `$${job.metadata.price}` : "Free"}
                                </div>
                              </td>
                              <td className={cn(enhancedPageLayoutStyles.table.td, user?.isAdmin ? "col-span-2" : "col-span-4")}>
                                <div className="px-6 py-4 text-sm text-gray-600 truncate">
                                  {job.author.name || job.author.email}
                                </div>
                              </td>
                              {user?.isAdmin && (
                                  <td className={cn(enhancedPageLayoutStyles.table.td, "col-span-2")}>
                                    <div className="px-6 py-4 flex justify-end gap-2" onClick={(e) => e?.stopPropagation()}>
                                      <EnhancedIconButton
                                        onClick={(e) => {
                                          e?.stopPropagation();
                                          navigate(`/jobs/${job.id}/edit`);
                                        }}
                                        icon={Edit}
                                        title="Edit"
                                      />
                                      <EnhancedIconButton
                                        onClick={(e) => {
                                          e?.stopPropagation();
                                          handleDelete(job.id);
                                        }}
                                        icon={Trash2}
                                        title="Delete"
                                        variant="danger"
                                      />
                                    </div>
                                  </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </EnhancedPageLayout>
    </DashboardLayout>
  );
}
